/**
 * PROG-IN-SPLIT/FE-EDITOR (#1c373689) — ExerciseProgressionEditor.
 * Architect addendum #d1460ba5 (95e928c4) §4.2-bis + §9-bis.
 *
 * Composes the already-shared library utils — does NOT touch
 * PrecompiledProgressionForm.tsx. Reuses ProgressionRuleEditor for the
 * per-set rule UI; reuses progressionEngine / precompiledChain /
 * precompiledRuleApply / precompiledNormalizer for the hypothetical
 * projections. Because the badges call the same engine functions on the
 * same shape, projection numbers match the library page by construction.
 *
 * Owned here (per §4.2-bis "implement the simpler equivalent rather than
 * refactoring the page file"):
 *   - week list / set grid layout
 *   - per-week / per-set chrome (add / remove)
 *   - hypothetical-load badge (computeHypLoad — local mirror of the
 *     library page's function, calling the same shared utils)
 *   - projection chain panel (simulateChain output)
 *
 * Modes:
 *   - "trainer-edit"    : full editing
 *   - "trainer-view"    : read-only display
 *   - "athlete-monitor" : workReps / workLoad inputs editable;
 *                         structure (rules / weeks / sets) read-only
 *
 * PROG-WEEK-PARAMS/FE-5 — the per-week and per-set JSX has been extracted
 * into sibling components (`ExerciseProgressionWeekRow`,
 * `ExerciseProgressionSetRow`). This file remains the data orchestrator:
 * it owns state, normalization, hypothetical-load computation, the option
 * caches, and the mutation helpers — which it threads down to the children.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Button, Empty, Grid, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { DragDropContext, Draggable, DropResult } from "react-beautiful-dnd";
import StrictModeDroppable from "../../common/StrictModeDroppable";
import type {
  PrecompiledSet,
  PrecompiledWeek,
  PrecompiledWeekProgressionRule,
  Tecnica,
} from "../../types/Entities";
import type { ExerciseProgression } from "../../types/Programmazione";
import {
  computeHypLoad,
  computeHypRepsGrid,
  normalizePastedRule,
  resolveSourceSlot,
  ruleIsEmpty,
} from "../../utils/precompiledRuleApply";
import { useClipboardOptional } from "../../contexts/clipboard";
import { normalizePrecompiledProgression } from "../../utils/precompiledNormalizer";
import type { ProgEditorOptions } from "../../hooks/useEntityOptions";
// PROG-WEEK-PARAMS/FE-2 — reuse the existing `pp-set-inherit-chip` class
// shipped with the library page; importing the page CSS makes it available
// wherever this editor is mounted (EditProgrammazione, Detail trainer-view).
import "../../pages/PrecompiledProgressions/PrecompiledProgressions.css";
import ExerciseProgressionWeekRow from "./ExerciseProgressionWeekRow";

// ─── helpers ──────────────────────────────────────────────────────────────────

const emptyRule = (): PrecompiledWeekProgressionRule => ({
  type: "fixed",
  loadComputeSource: null,
  initialLoadType: "fixed",
  initialLoad: null,
  initialWnRm: null,
  initialPnRm: null,
  weekPriority: null,
  incrementDirection: null,
  incrementTarget: null,
  repsChange: null,
  loadChangeType: null,
  loadChangeValue: null,
  weekWnRm: null,
  weekPnRm: null,
  progressionType: null,
  positiveThresholdsStS: null,
  negativeThresholdsStS: null,
  positiveThresholdsSiS: null,
  negativeThresholdsSiS: null,
  matrixSource: null,
  calculationPriority: null,
  weekPrioritySiS: null,
  incrementDirectionSiS: null,
  incrementTargetSiS: null,
  repsChangeSiS: null,
  loadChangeTypeSiS: null,
  loadChangeValueSiS: null,
  weekWnRmSiS: null,
  weekPnRmSiS: null,
  matrixSourceSiS: null,
});

const emptyWeek = (weekNumber: number): PrecompiledWeek => ({
  weekNumber,
  repSchemeTypeId: 0,
  repScheme: "",
  effortTypeId: 0,
  effort: "",
  // PROG-WEEK-PARAMS/FE-1 Q1 — match the library default ("select") so newly
  // added weeks land on the catalog-bound Select rather than free-text.
  tutMode: "select",
  tut: null,
  tecnicaMode: "select",
  tecnica: null,
  xRepsTarget: null,
  xRepsLoadDropPerc: null,
  progressionRule: emptyRule(),
  sets: [],
});

// #0d0eedce — stable module-level empty fallback for the tecniche catalog.
// Using a single frozen array gives `lookupTecnica`'s `useCallback` dep a
// stable identity without invoking `useMemo` inside the component (the
// previous #e0359d7f wrap tripped `react-hooks/rules-of-hooks` in some lint
// environments). `as const` + the `ReadonlyArray` cast pin the reference
// identity so dep arrays consuming `options?.tecnicheRaw ?? EMPTY_TECNICHE`
// only change when `options?.tecnicheRaw` itself changes.
const EMPTY_TECNICHE: ReadonlyArray<Tecnica> = Object.freeze([]) as ReadonlyArray<Tecnica>;

const emptySet = (): PrecompiledSet => ({
  label: null,
  trialLoad: null,
  progressionRule: emptyRule(),
});

// #4d749bac — local `computeHypLoad` collapsed into the shared
// `precompiledRuleApply.computeHypLoad` (V2/3 Bug 3 fix: dispatch via
// `effectiveLoadComputeSource` so `progressionType`-only rules render
// instead of falling into `default → null`). Re-exported under the same
// name so existing consumers — including the editor's own test suite at
// `__tests__/ExerciseProgressionEditor.test.tsx` — continue to import
// from this module without churn. `computeHypLoadGrid` stays local; it
// wraps the shared `computeHypLoad` with the V3/1 walk-back synthesis
// pattern that lives in this file.
export { computeHypLoad };

/**
 * Returns a (weekIdx × setIdx) grid of hypothetical loads. Same algorithm as
 * the library page (`weekHypLoads` memo at PrecompiledProgressionForm.tsx
 * lines 2040-2071) — guarantees badge parity for the same input shape.
 */
export function computeHypLoadGrid(
  weeks: PrecompiledWeek[],
): (number | null)[][] {
  const result: (number | null)[][] = [];
  weeks.forEach((week, wi) => {
    const row: (number | null)[] = [];
    const sets = week.sets ?? [];
    sets.forEach((set, si) => {
      // PROG-ENGINE-V3/1 — per-axis source-slot resolution is unconditional
      // (not gated on `ruleIsEmpty`). Decouples "this slot has its own
      // dispatch" from "the source-axis rule lives N slots back via
      // applyForNext*". `resolveSourceSlot` falls back to immediate prev so
      // the offset=1 legacy path is preserved when no ancestor covers.
      const stsRes = resolveSourceSlot(weeks, wi, si, "sts", set.progressionRule);
      const sisRes = resolveSourceSlot(weeks, wi, si, "sis", set.progressionRule);
      // When the slot's own rule has no dispatch-driving content but a
      // covering ancestor exists, synthesize an effective rule that pins
      // loadComputeSource onto the resolved axes — preserves the legacy
      // "empty intermediate inherits ancestor's axes" semantic.
      // When the slot's own rule is empty AND walk-back found a source with
      // its own content on an axis, synthesize an effective rule that pins
      // loadComputeSource onto the resolved axes. Mirrors the legacy
      // "empty intermediate inherits ancestor's dispatch" semantic. When the
      // own rule already has content (incl. just `loadComputeSource`), the
      // user's dispatch wins — but `prevWeekSetRule`/`prevSetRule` below
      // still come from walk-back, which is the V3/1 dispatch-site fix.
      let effectiveRule = set.progressionRule;
      const stsHasInheritedContent =
        stsRes != null && stsRes.rule != null && !ruleIsEmpty(stsRes.rule);
      const sisHasInheritedContent =
        sisRes != null && sisRes.rule != null && !ruleIsEmpty(sisRes.rule);
      if (
        ruleIsEmpty(set.progressionRule) &&
        (stsHasInheritedContent || sisHasInheritedContent)
      ) {
        const base =
          (stsHasInheritedContent ? stsRes!.rule : null) ??
          (sisHasInheritedContent ? sisRes!.rule : null) ??
          set.progressionRule;
        effectiveRule = { ...base };
        if (stsHasInheritedContent && sisHasInheritedContent) {
          effectiveRule.loadComputeSource = "both";
        } else if (stsHasInheritedContent) {
          effectiveRule.loadComputeSource = "sts";
        } else if (sisHasInheritedContent) {
          effectiveRule.loadComputeSource = "sis";
        }
      }
      const prevWeekLoadRaw = wi > 0 ? result[wi - 1]?.[si] ?? null : null;
      const prevWeekSetRaw = wi > 0 ? weeks[wi - 1].sets?.[si] ?? null : null;
      const prevSetLoadRaw = si > 0 ? row[si - 1] ?? null : null;
      const prevSetInWeekRaw = si > 0 ? sets[si - 1] ?? null : null;
      // For offset>1 (covering ancestor case), the immediate prev slot is an
      // empty intermediate. Synthesize a prev-slot view carrying the source's
      // seed and the source-axis rule, so both the threshold-inherit path
      // and the fixed/wn_rm fallback anchor on the SOURCE (not the prior
      // chain link) — #cda06c24 fixes the pre-existing compounding bug.
      const stsSourceSet =
        stsRes ? weeks[stsRes.sourceWi]?.sets?.[stsRes.sourceSi] ?? null : null;
      const sisSourceSet =
        sisRes ? weeks[sisRes.sourceWi]?.sets?.[sisRes.sourceSi] ?? null : null;
      // #cda06c24 — source-seed extraction. Used both as the synthetic prev
      // slot's workLoad AND as the load argument passed to `computeHypLoad`
      // when the offset>1 walk-back path fires, so the axis rule applies
      // ONCE against the seed for every covered slot.
      const sourceSeedLoad = (set: PrecompiledSet | null): number | null =>
        set?.workLoad ?? set?.progressionRule?.initialLoad ?? set?.trialLoad ?? null;
      const stsFallbackLoad = sourceSeedLoad(stsSourceSet);
      const sisFallbackLoad = sourceSeedLoad(sisSourceSet);
      const synthPrev = (
        baseSet: PrecompiledSet | null,
        sourceSet: PrecompiledSet | null,
        // `chainLoad` — deprecated by #cda06c24, anchoring is source-seed
        // only (compounding-bug fix). Kept in the signature so the three
        // mirror call sites (#f7275eb3 collapse pending) keep identical
        // shape; intentionally unused here.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        chainLoad: number | null,
        axisRule: PrecompiledWeekProgressionRule | null,
      ): PrecompiledSet | null => {
        // #a6cfa9f3 / #cda06c24 — when the intermediate slot's hypLoad is
        // null (no upstream baseLoad), the synthetic prev anchors to the
        // SOURCE seed (workLoad → initialLoad → trialLoad), NOT to the
        // prior chain link's projection. Anchoring to chainLoad caused
        // compounding (set 0 +5kg, applyForNextSets=2 → set 2 got 70
        // instead of 65). Threshold-mode path via `materializePrevSlot`
        // (#f38a51f4) reads the same source seed → consistent anchor.
        const effectiveLoad = sourceSeedLoad(sourceSet);
        if (effectiveLoad == null) return baseSet;
        return {
          ...(baseSet ?? emptySet()),
          workReps: sourceSet?.workReps ?? baseSet?.workReps ?? null,
          workLoad: effectiveLoad,
          repScheme: sourceSet?.repScheme ?? baseSet?.repScheme ?? null,
          progressionRule: axisRule ?? baseSet?.progressionRule ?? emptyRule(),
        };
      };
      const stsOffset = stsRes?.sourceOffset ?? 0;
      const sisOffset = sisRes?.sourceOffset ?? 0;
      const prevWeekSet =
        stsOffset > 1
          ? synthPrev(prevWeekSetRaw, stsSourceSet, prevWeekLoadRaw, stsRes?.rule ?? null)
          : prevWeekSetRaw;
      const prevSetInWeek =
        sisOffset > 1
          ? synthPrev(prevSetInWeekRaw, sisSourceSet, prevSetLoadRaw, sisRes?.rule ?? null)
          : prevSetInWeekRaw;
      // #cda06c24 / #615019e1 — anchor the load arg to `computeHypLoad` on
      // the source seed whenever a source is resolved, at any offset
      // (including 1). The pre-#615019e1 gate `stsOffset > 1` left
      // offset=1 reading `row[si-1]` which traces to
      // `computeInitialBaseline` and ignores `workLoad` — asymmetric with
      // the offset>1 (synthPrev) path. Unifying both paths anchors rule
      // application to the source seed at every covered offset, so a set
      // 0 with a logged `workLoad` propagates to both set 1 (offset=1)
      // and set 2 (offset=2) consistently. When `seedLoadFromSource`
      // returns null (no seed on source), falls through to the raw
      // chain-link value — preserves the no-seed regression case.
      const seedLoadFromSource = sourceSeedLoad;
      const prevWeekLoad =
        stsRes != null
          ? (seedLoadFromSource(stsSourceSet) ?? prevWeekLoadRaw)
          : prevWeekLoadRaw;
      const prevSetLoad =
        sisRes != null
          ? (seedLoadFromSource(sisSourceSet) ?? prevSetLoadRaw)
          : prevSetLoadRaw;
      const prevWeekSetRule =
        stsRes?.rule ?? prevWeekSetRaw?.progressionRule ?? null;
      const prevSetRule =
        sisRes?.rule ?? prevSetInWeekRaw?.progressionRule ?? null;
      row.push(
        computeHypLoad(
          effectiveRule,
          wi === 0,
          si,
          prevWeekLoad,
          prevWeekSetRule,
          prevSetLoad,
          prevSetRule,
          prevWeekSet,
          prevSetInWeek,
          week.repScheme,
        ),
      );
    });
    result.push(row);
  });
  return result;
}

// ─── component types ──────────────────────────────────────────────────────────

export type ExerciseProgressionEditorMode =
  | "trainer-edit"
  | "trainer-view"
  | "athlete-monitor";

export interface ExerciseProgressionEditorProps {
  value: ExerciseProgression;
  onChange: (next: ExerciseProgression) => void;
  disabled?: boolean;
  mode: ExerciseProgressionEditorMode;
  /** PROG-WEEK-PARAMS/FE-1 — page-lifted catalogs + per-type fetchers.
   *  When omitted (legacy / standalone consumers) the typed Select controls
   *  fall back to render-but-empty; the free-text W2/W4 path still works
   *  via the underlying repScheme/effort strings. */
  options?: ProgEditorOptions;
}

// ─── component ────────────────────────────────────────────────────────────────

export default function ExerciseProgressionEditor({
  value,
  onChange,
  disabled,
  mode,
  options,
}: ExerciseProgressionEditorProps) {
  // Read-time normalization mirrors the library page: copy week-level params
  // down onto per-set fields so legacy / library-imported snapshots render
  // uniformly. Pure / idempotent.
  const normalized = useMemo(
    () => normalizePrecompiledProgression({ label: "", weeks: value.weeks }),
    [value.weeks],
  );
  const weeks = normalized.weeks;

  // Edit gates: structure (rules, weeks, sets, repScheme) is editable only in
  // trainer-edit. Work data (workReps / workLoad) is editable in trainer-edit
  // AND athlete-monitor. `disabled` prop hard-locks everything.
  const structureLocked = disabled || mode !== "trainer-edit";
  const workDataLocked =
    disabled || (mode !== "trainer-edit" && mode !== "athlete-monitor");
  // PROG-SET-MEDIA/FE-EDITOR-1 — note + media are part of the work-data
  // layer (Q4): both trainer-edit and athlete-monitor can attach. Aliased
  // to workDataLocked so future gate changes stay in sync.
  const mediaLocked = workDataLocked;

  // PROG-UX/IMPL — `isCompact` (<sm) for E2/E3 density decisions.
  const screens = Grid.useBreakpoint();
  const isCompact = !screens.sm;

  // Whole-set / whole-week copy-paste reuses the shared clipboard slots
  // (`copiedPrecompiledSet` / `copiedPrecompiledWeek`) — same slots the
  // library page (PrecompiledProgressionForm) writes to, so a set/week copied
  // there pastes here and vice-versa. Copy is allowed whenever the editor is
  // interactive; paste is gated on `!structureLocked` (see handlers below).
  // useClipboardOptional so the editor can also mount outside a
  // ClipboardProvider (isolated unit tests) — copy/paste simply no-ops then.
  const clipboard = useClipboardOptional();
  const copiedPrecompiledSet = clipboard?.copiedPrecompiledSet ?? null;
  const copyPrecompiledSet = clipboard?.copyPrecompiledSet;
  const copiedPrecompiledWeek = clipboard?.copiedPrecompiledWeek ?? null;
  const copyPrecompiledWeek = clipboard?.copyPrecompiledWeek;

  const hypLoadGrid = useMemo(() => computeHypLoadGrid(weeks), [weeks]);
  // PROG-BUGS/ENGINE-3 (Bug A) — reps grid mirrors hypLoadGrid so the per-set
  // header pill stays coherent with the body's "Ipotetico (da sorgente)" badge.
  const hypRepsGrid = useMemo(() => computeHypRepsGrid(weeks), [weeks]);

  // PROG-WEEK-PARAMS/FE-3 — tecnica gating lookup. `options.tecnicheRaw` is
  // the catalog injected by the page-level useEntityOptions; resolve a tecnica
  // value (stored as id-as-string or as name) to its flags.
  // #0d0eedce — fall back to the frozen module-level `EMPTY_TECNICHE` (above)
  // instead of an inline `[]` so the `useCallback` dep below has a stable
  // identity when the catalog is absent, without invoking `useMemo` here.
  const tecnicheRaw = options?.tecnicheRaw ?? EMPTY_TECNICHE;
  const lookupTecnica = useCallback(
    (val: string | number | null | undefined) => {
      if (val == null || val === "") return undefined;
      const s = String(val);
      return tecnicheRaw.find(
        (t) => String((t as any).id) === s || (t as any).name === s,
      );
    },
    [tecnicheRaw],
  );

  // PROG-WEEK-PARAMS/FE-1 — per-week catalogs for the cascading W2 (repScheme)
  // and W4 (effort) AutoComplete/Select. Hydrated on `repSchemeTypeId` /
  // `effortTypeId` change via the page-level cached fetchers; the underlying
  // request is deduped by typeId in useEntityOptions so multiple weeks (and
  // multiple editor instances) sharing a typeId only fetch once.
  const [weekRepSchemeOpts, setWeekRepSchemeOpts] = useState<
    Record<number, { label: string; value: string }[]>
  >({});
  const [weekEffortOpts, setWeekEffortOpts] = useState<
    Record<number, { label: string; value: string }[]>
  >({});

  // PROG-WEEK-PARAMS/FE-2 — per-set scheme/effort catalogs keyed by `${wi}-${si}`.
  // Backed by the same per-typeId cache in useEntityOptions, so calling these
  // for many sets sharing a typeId is free.
  const [setRepSchemeOpts, setSetRepSchemeOpts] = useState<
    Record<string, { label: string; value: string }[]>
  >({});
  const [setEffortOpts, setSetEffortOpts] = useState<
    Record<string, { label: string; value: string }[]>
  >({});

  // Track current typeIds per week to skip redundant fetches when other,
  // unrelated fields change.
  const repTypeIdsRef = useRef<Record<number, number | undefined>>({});
  const effTypeIdsRef = useRef<Record<number, number | undefined>>({});
  const setRepTypeIdsRef = useRef<Record<string, number | undefined>>({});
  const setEffTypeIdsRef = useRef<Record<string, number | undefined>>({});

  useEffect(() => {
    if (!options) return;
    weeks.forEach((w, wi) => {
      const rTid = w.repSchemeTypeId || undefined;
      if (rTid !== repTypeIdsRef.current[wi]) {
        repTypeIdsRef.current[wi] = rTid;
        if (rTid) {
          options.loadRepSchemesByType(rTid).then((opts) => {
            setWeekRepSchemeOpts((prev) => ({ ...prev, [wi]: opts }));
          });
        } else {
          setWeekRepSchemeOpts((prev) => {
            if (!(wi in prev)) return prev;
            const { [wi]: _, ...rest } = prev;
            return rest;
          });
        }
      }
      const eTid = w.effortTypeId || undefined;
      if (eTid !== effTypeIdsRef.current[wi]) {
        effTypeIdsRef.current[wi] = eTid;
        if (eTid) {
          options.loadEffortsByType(eTid).then((opts) => {
            setWeekEffortOpts((prev) => ({ ...prev, [wi]: opts }));
          });
        } else {
          setWeekEffortOpts((prev) => {
            if (!(wi in prev)) return prev;
            const { [wi]: _, ...rest } = prev;
            return rest;
          });
        }
      }
      // PROG-WEEK-PARAMS/FE-2 — hydrate per-set catalogs for the raw (un-
      // normalized) per-set typeIds. Pulling from value.weeks (raw) so a set
      // that inherits from the week stays "uncached" until the user actively
      // picks a per-set typeId.
      const rawWeek = value.weeks?.[wi];
      const rawSets = rawWeek?.sets ?? [];
      rawSets.forEach((rawSet, si) => {
        const key = `${wi}-${si}`;
        const rsTid = rawSet.repSchemeTypeId || undefined;
        if (rsTid !== setRepTypeIdsRef.current[key]) {
          setRepTypeIdsRef.current[key] = rsTid;
          if (rsTid) {
            options.loadRepSchemesByType(rsTid).then((opts) => {
              setSetRepSchemeOpts((prev) => ({ ...prev, [key]: opts }));
            });
          } else {
            setSetRepSchemeOpts((prev) => {
              if (!(key in prev)) return prev;
              const { [key]: _, ...rest } = prev;
              return rest;
            });
          }
        }
        const esTid = rawSet.effortTypeId || undefined;
        if (esTid !== setEffTypeIdsRef.current[key]) {
          setEffTypeIdsRef.current[key] = esTid;
          if (esTid) {
            options.loadEffortsByType(esTid).then((opts) => {
              setSetEffortOpts((prev) => ({ ...prev, [key]: opts }));
            });
          } else {
            setSetEffortOpts((prev) => {
              if (!(key in prev)) return prev;
              const { [key]: _, ...rest } = prev;
              return rest;
            });
          }
        }
      });
    });
  }, [weeks, options, value.weeks]);

  // ─── mutation helpers (no-ops when structureLocked) ─────────────────────────

  const emit = useCallback(
    (next: PrecompiledWeek[]) => {
      onChange({ ...value, weeks: next });
    },
    [onChange, value],
  );

  const addWeek = useCallback(() => {
    if (structureLocked) return;
    emit([...weeks, emptyWeek(weeks.length + 1)]);
  }, [structureLocked, weeks, emit]);

  const removeWeek = useCallback(
    (wi: number) => {
      if (structureLocked) return;
      const next = weeks
        .filter((_, i) => i !== wi)
        .map((w, i) => ({ ...w, weekNumber: i + 1 }));
      emit(next);
    },
    [structureLocked, weeks, emit],
  );

  // PROG-DND/DND-1 — reorder weeks via drag handle. Renumber `weekNumber`
  // so downstream consumers (engine, Detail view chips, BE persistence)
  // continue to see contiguous 1..N week numbers. No-op when locked.
  const reorderWeeks = useCallback(
    (from: number, to: number) => {
      if (structureLocked) return;
      if (from === to) return;
      if (from < 0 || to < 0 || from >= weeks.length || to >= weeks.length)
        return;
      const next = [...weeks];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      const renumbered = next.map((w, i) => ({ ...w, weekNumber: i + 1 }));
      emit(renumbered);
    },
    [structureLocked, weeks, emit],
  );

  // PROG-DND/DND-2 — reorder sets inside a single week via drag handle.
  // Sets carry no stable identifier and downstream consumers index them by
  // position, so splice is enough. No-op when locked.
  const reorderSets = useCallback(
    (wi: number, from: number, to: number) => {
      if (structureLocked) return;
      if (from === to) return;
      const week = weeks[wi];
      if (!week) return;
      const sets = [...(week.sets ?? [])];
      if (
        from < 0 ||
        to < 0 ||
        from >= sets.length ||
        to >= sets.length
      )
        return;
      const [moved] = sets.splice(from, 1);
      sets.splice(to, 0, moved);
      const nextWeeks = weeks.map((w, i) => (i === wi ? { ...w, sets } : w));
      emit(nextWeeks);
    },
    [structureLocked, weeks, emit],
  );

  const updateSet = useCallback(
    (wi: number, si: number, patch: Partial<PrecompiledSet>) => {
      const week = weeks[wi];
      if (!week) return;
      const sets = (week.sets ?? []).map((s, i) =>
        i === si ? { ...s, ...patch } : s,
      );
      const nextWeeks = weeks.map((w, i) =>
        i === wi ? { ...w, sets } : w,
      );
      emit(nextWeeks);
    },
    [weeks, emit],
  );

  const updateSetRule = useCallback(
    (
      wi: number,
      si: number,
      partial: Partial<PrecompiledWeekProgressionRule>,
    ) => {
      if (structureLocked) return;
      const set = weeks[wi]?.sets?.[si];
      if (!set) return;
      updateSet(wi, si, {
        progressionRule: { ...set.progressionRule, ...partial },
      });
    },
    [structureLocked, weeks, updateSet],
  );

  const addSet = useCallback(
    (wi: number) => {
      if (structureLocked) return;
      const week = weeks[wi];
      if (!week) return;
      const sets = [...(week.sets ?? []), emptySet()];
      const nextWeeks = weeks.map((w, i) => (i === wi ? { ...w, sets } : w));
      emit(nextWeeks);
    },
    [structureLocked, weeks, emit],
  );

  const removeSet = useCallback(
    (wi: number, si: number) => {
      if (structureLocked) return;
      const week = weeks[wi];
      if (!week) return;
      const sets = (week.sets ?? []).filter((_, i) => i !== si);
      const nextWeeks = weeks.map((w, i) => (i === wi ? { ...w, sets } : w));
      emit(nextWeeks);
    },
    [structureLocked, weeks, emit],
  );

  const updateWeekField = useCallback(
    <K extends keyof PrecompiledWeek>(
      wi: number,
      field: K,
      val: PrecompiledWeek[K],
    ) => {
      if (structureLocked) return;
      const nextWeeks = weeks.map((w, i) =>
        i === wi ? { ...w, [field]: val } : w,
      );
      emit(nextWeeks);
    },
    [structureLocked, weeks, emit],
  );

  // PROG-WEEK-PARAMS/FE-2 — structure-gated multi-field set patch for the
  // per-set W1-W8 mirror. Mirrors patchWeek for the clearing rules at set
  // level (typeId change → null scheme/effort; mode flip → null value).
  const patchSetStructure = useCallback(
    (wi: number, si: number, patch: Partial<PrecompiledSet>) => {
      if (structureLocked) return;
      updateSet(wi, si, patch);
    },
    [structureLocked, updateSet],
  );

  // PROG-WEEK-PARAMS/FE-1 — multi-field week patch, used by clearing rules
  // (e.g. typeId change must null the corresponding scheme/effort in the
  // same emit so React state stays consistent with the cascade UI).
  const patchWeek = useCallback(
    (wi: number, patch: Partial<PrecompiledWeek>) => {
      if (structureLocked) return;
      const nextWeeks = weeks.map((w, i) => (i === wi ? { ...w, ...patch } : w));
      emit(nextWeeks);
    },
    [structureLocked, weeks, emit],
  );

  // ─── whole-set / whole-week copy-paste ──────────────────────────────────────
  // Mirrors the library page's pasteSet / pasteWeek semantics
  // (PrecompiledProgressionForm.tsx): deep-clone the clipboard slot, run
  // `normalizePastedRule` so threshold gating fields are back-filled, then
  // replace the target set/week. Copy has no gate; paste is structure-gated.

  const copySet = useCallback(
    (wi: number, si: number) => {
      const set = weeks[wi]?.sets?.[si];
      if (!set) return;
      copyPrecompiledSet?.(set);
    },
    [weeks, copyPrecompiledSet],
  );

  const pasteSet = useCallback(
    (wi: number, si: number) => {
      if (structureLocked || !copiedPrecompiledSet) return;
      const week = weeks[wi];
      if (!week) return;
      const cloned: PrecompiledSet = JSON.parse(
        JSON.stringify(copiedPrecompiledSet),
      );
      if (cloned.progressionRule) {
        cloned.progressionRule = normalizePastedRule(cloned.progressionRule);
      }
      const sets = (week.sets ?? []).map((s, i) => (i === si ? cloned : s));
      emit(weeks.map((w, i) => (i === wi ? { ...w, sets } : w)));
    },
    [structureLocked, copiedPrecompiledSet, weeks, emit],
  );

  const copyWeek = useCallback(
    (wi: number) => {
      const week = weeks[wi];
      if (!week) return;
      copyPrecompiledWeek?.(week);
    },
    [weeks, copyPrecompiledWeek],
  );

  const pasteWeek = useCallback(
    (wi: number) => {
      if (structureLocked || !copiedPrecompiledWeek) return;
      if (!weeks[wi]) return;
      const cloned: PrecompiledWeek = JSON.parse(
        JSON.stringify(copiedPrecompiledWeek),
      );
      cloned.weekNumber = wi + 1;
      (cloned.sets ?? []).forEach((s) => {
        if (s.progressionRule)
          s.progressionRule = normalizePastedRule(s.progressionRule);
      });
      emit(weeks.map((w, i) => (i === wi ? cloned : w)));
    },
    [structureLocked, copiedPrecompiledWeek, weeks, emit],
  );

  // PROG-DND/DND-1 — stable, instance-unique droppable id. Generated via
  // React's useId so two editors on the same page (rare but possible inside
  // a multi-exercise form) never collide; also avoids collision with the
  // outer Splits/Exercises DnD on the Detail page (which uses ids like
  // `view-splits` / `view-exercises-N`).
  const dndId = useId();
  const weeksDroppableId = `editor-weeks-${dndId}`;
  const handleWeekDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      reorderWeeks(result.source.index, result.destination.index);
    },
    [reorderWeeks],
  );

  // ─── render ─────────────────────────────────────────────────────────────────

  if (weeks.length === 0) {
    return (
      <div data-testid="progression-editor-empty">
        <Empty description="Nessuna settimana — aggiungi la prima per iniziare" />
        {!structureLocked && (
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addWeek}
              data-testid="progression-editor-add-first-week"
            >
              Aggiungi Settimana
            </Button>
          </div>
        )}
      </div>
    );
  }

  // PROG-DND/DND-1 — when locked, render the legacy AntD Space layout so we
  // don't pay the DnD wrapper cost on read-only mounts (Detail trainer-view,
  // athlete-monitor, disabled prop) and keep the DOM identical to pre-DND-1
  // for existing tests/snapshots.
  const renderWeekRow = (
    week: PrecompiledWeek,
    wi: number,
    dragHandleProps?:
      | import("react-beautiful-dnd").DraggableProvidedDragHandleProps
      | null,
  ) => (
    <ExerciseProgressionWeekRow
      wi={wi}
      week={week}
      weeks={weeks}
      dragHandleProps={dragHandleProps}
      rawWeeks={value.weeks}
      hypLoadGrid={hypLoadGrid}
      hypRepsGrid={hypRepsGrid}
      structureLocked={structureLocked}
      workDataLocked={workDataLocked}
      mediaLocked={mediaLocked}
      mode={mode}
      isCompact={isCompact}
      options={options}
      weekRepSchemeOpts={weekRepSchemeOpts[wi] ?? []}
      weekEffortOpts={weekEffortOpts[wi] ?? []}
      setRepSchemeOptsByKey={setRepSchemeOpts}
      setEffortOptsByKey={setEffortOpts}
      lookupTecnica={lookupTecnica}
      onRemoveWeek={() => removeWeek(wi)}
      onCopyWeek={() => copyWeek(wi)}
      onPasteWeek={() => pasteWeek(wi)}
      canPasteWeek={!!copiedPrecompiledWeek}
      onCopySet={(si) => copySet(wi, si)}
      onPasteSet={(si) => pasteSet(wi, si)}
      canPasteSet={!!copiedPrecompiledSet}
      onPatchWeek={(patch) => patchWeek(wi, patch)}
      onUpdateWeekField={(field, val) => updateWeekField(wi, field, val)}
      onAddSet={() => addSet(wi)}
      onRemoveSet={(si) => removeSet(wi, si)}
      onPatchSet={(si, patch) => updateSet(wi, si, patch)}
      onPatchSetStructure={(si, patch) => patchSetStructure(wi, si, patch)}
      onUpdateSetRule={(si, partial) => updateSetRule(wi, si, partial)}
      onReorderSets={(from, to) => reorderSets(wi, from, to)}
    />
  );

  return (
    <div data-testid="progression-editor">
      {structureLocked ? (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {weeks.map((week, wi) => (
            <div key={wi}>{renderWeekRow(week, wi)}</div>
          ))}
        </Space>
      ) : (
        <DragDropContext onDragEnd={handleWeekDragEnd}>
          <StrictModeDroppable droppableId={weeksDroppableId}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 24,
                  width: "100%",
                }}
              >
                {weeks.map((week, wi) => (
                  <Draggable
                    key={wi}
                    draggableId={`${weeksDroppableId}-${wi}`}
                    index={wi}
                  >
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        data-testid={`progression-week-draggable-${wi}`}
                      >
                        {renderWeekRow(week, wi, dragProvided.dragHandleProps)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      )}

      {!structureLocked && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={addWeek}
          block
          data-testid="progression-editor-add-week"
          style={{ marginTop: 24 }}
        >
          Aggiungi Settimana
        </Button>
      )}
    </div>
  );
}
